import { downloadBlobFromApi, openPostedBlobFromApi } from '../../helpers'
import apiService from '../Api'
import {
  AttendeesCheckinResponse,
  CancelOrderResponse,
  CaucaoParticipantsResponse,
  CertificateLayout,
  CheckoutCatalogEstablishment,
  CheckoutInfo,
  CheckoutPaymentResult,
  EstablishmentBankFields,
  EstablishmentFeeType,
  EstablishmentProductKind,
  EventCertificate,
  EventCertificateSchedule,
  EventCertificateSchedulePayload,
  EventCertificatesPayload,
  EventCertificatesStats,
  EventComplete,
  EventContestOrderDetail,
  EventDetail,
  EventFormat,
  EventListItem,
  EventOrderDetail,
  EventOrderSourceOption,
  EventType,
  OrdersResponse,
  ParkingTicketTypesResponse,
  ParticipantsResponse,
  RevenueSummary,
  SelectEvent
} from './types'

export interface ITicketFields {
  id?: number
  type: 'PAID' | 'FREE'
  title: string
  quantity: number
  price: number
  sales_start: string
  sales_end: string
  max_per_purchase: number
  description: string
  visible: boolean
  fee_exempt: boolean
  is_child_ticket: boolean
  /** Vender pelo app de check-in na porta (ABRA-319). */
  sell_at_door: boolean
  /** Preço na porta. Null é o campo vazio — com a venda na porta ligada, a API recusa. */
  door_price: number | null
}

/** Produto/serviço de um estabelecimento, como o formulário envia (ABRA-317). */
export interface IEstablishmentProductFields {
  id?: number
  name: string
  /** Oficina pede o nome de cada inscrito na venda (ABRA-399). */
  kind: EstablishmentProductKind
  /** Null é o campo vazio — a API recusa, preço é obrigatório. 0 é o produto gratuito (ABRA-399). */
  price: number | null
  /** `''` é estoque ilimitado. */
  stock: number | ''
  /** Máximo por pessoa (ABRA-399); `''` é sem limite. */
  max_per_buyer: number | ''
  /** `''` é "ainda não escolhido": não há tipo padrão. */
  fee_type: EstablishmentFeeType | ''
  fee_value: number | null
}

/** Estabelecimento (o "parceiro" do SDD) com seus produtos e dados bancários (ABRA-317). */
export interface IEstablishmentFields extends EstablishmentBankFields {
  id?: number
  name: string
  products: IEstablishmentProductFields[]
}

const ESTABLISHMENT_BANK_FIELDS: (keyof EstablishmentBankFields)[] = [
  'bank_code',
  'bank_name',
  'bank_agency',
  'bank_account',
  'bank_holder_name',
  'pix_key_type',
  'pix_key'
]

/**
 * Escreve estabelecimentos no multipart como `key[i][campo]` e `key[i][products][j][campo]`.
 * Usado pelo formulário do evento (`establishments`) e pelo da série (`establishment_templates`).
 * Vazio vai como `''`, que o Laravel converte em null — estoque vazio é ilimitado, e a taxa de
 * um produto "Sem taxa" nunca leva valor.
 */
export function appendEstablishments(formData: FormData, key: string, establishments: IEstablishmentFields[]): void {
  establishments.forEach((establishment, i) => {
    const prefix = `${key}[${i}]`
    if (establishment.id) {
      formData.append(`${prefix}[id]`, establishment.id.toString())
    }
    formData.append(`${prefix}[name]`, establishment.name)
    ESTABLISHMENT_BANK_FIELDS.forEach((field) => {
      formData.append(`${prefix}[${field}]`, establishment[field] ?? '')
    })

    establishment.products.forEach((product, j) => {
      const productPrefix = `${prefix}[products][${j}]`
      if (product.id) {
        formData.append(`${productPrefix}[id]`, product.id.toString())
      }
      formData.append(`${productPrefix}[name]`, product.name)
      formData.append(`${productPrefix}[kind]`, product.kind)
      formData.append(`${productPrefix}[price]`, product.price === null ? '' : product.price.toString())
      formData.append(`${productPrefix}[stock]`, product.stock === '' ? '' : product.stock.toString())
      formData.append(
        `${productPrefix}[max_per_buyer]`,
        product.max_per_buyer === '' ? '' : product.max_per_buyer.toString()
      )
      formData.append(`${productPrefix}[fee_type]`, product.fee_type)
      formData.append(
        `${productPrefix}[fee_value]`,
        product.fee_type === 'none' || product.fee_type === 'no_payout' || product.fee_value === null
          ? ''
          : product.fee_value.toString()
      )
    })
  })
}

export interface IProfessionalFields {
  _id: string
  teacher_id?: number | null
  title: string
  name: string
  bio: string
  thumb: File | null
  thumbPreview: string | null
}

export interface IFaqFields {
  _id: string
  id?: number
  question: string
  answer: string
}

export interface IEventFields {
  name: string
  slug: string
  format: EventFormat | ''
  thumb: File | null
  thumb_wide: File | null
  // Event video: an uploaded MP4 (new file) instead of a Vimeo link. `remove_video`
  // clears the stored video without uploading a new one.
  video: File | null
  remove_video: boolean
  date_start: string
  is_multiday: boolean
  date_end: string
  time_start: string
  time_end: string
  visible: boolean
  use_nfc: boolean
  types: number[]
  /** Cursos relacionados. Vazio é válido: o evento não aparece em página de curso. */
  course_ids: number[]
  copy: string
  target_audience: string
  topics: string
  professionals: IProfessionalFields[]
  tickets: ITicketFields[]
  zipcode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  latitude: number | null
  longitude: number | null
  min_installment_value: number | null
  // ABRA-252: ticket do Abra Park que vende o estacionamento do evento.
  // Null desliga a associação e devolve o valor ao preenchimento manual.
  parking_ticket_type_id: number | null
  place_id?: string | null
  place_name?: string | null
  use_term: boolean
  term: string | null
  use_testimonial: boolean
  testimonial_title: string | null
  // Testimonial videos, per slot (final order). A slot is either a newly uploaded
  // MP4 (`testimonial_videos[i]`) or a kept stored path/URL (`testimonial_existing[i]`).
  testimonial_videos: (File | null)[]
  testimonial_existing: (string | null)[]
  active_campaign_lead_lists: string[]
  active_campaign_buyer_lists: string[]
  use_help_form: boolean
  faqs: IFaqFields[]
  /** "Produtos e Serviços" (ABRA-317). */
  establishments: IEstablishmentFields[]
}

export class EventService {
  async list(queryParams: {
    search: string
    length: number
    page: number
    status?: string
    type?: number
    date_from?: string
    date_to?: string
    view?: 'events' | 'series'
  }): Promise<{ data: EventListItem[]; total_filtered: number; total: number }> {
    const params = new URLSearchParams()
    params.append('search', queryParams.search)
    params.append('page_size', queryParams.length.toString())
    params.append('page', queryParams.page.toString())

    if (queryParams.view) {
      params.append('view', queryParams.view)
    }

    if (queryParams.status) {
      params.append('status', queryParams.status)
    }

    if (queryParams.type) {
      params.append('type', queryParams.type.toString())
    }

    if (queryParams.date_from) {
      params.append('date_from', queryParams.date_from)
    }

    if (queryParams.date_to) {
      params.append('date_to', queryParams.date_to)
    }

    return apiService.get('/events', { params }).then((response) => response.data)
  }

  async get(id: number): Promise<EventComplete> {
    return apiService.get(`/events/${id}`).then((response) => response.data)
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`/events/${id}`)
  }

  async toggleVisibility(id: number): Promise<void> {
    await apiService.patch(`/events/${id}/toggle-visibility`)
  }

  async getTypes(): Promise<EventType[]> {
    return apiService.get('/events/types').then((response) => response.data)
  }

  async getActiveCampaignLists(): Promise<{ id: string; name: string }[]> {
    return apiService.get('/events/active-campaign-lists').then((response) => response.data.data)
  }

  async create(form: IEventFields): Promise<EventComplete> {
    const formData = this.buildFormData(form)

    formData.append('use_term', form.use_term ? '1' : '0')
    if (form.use_term) {
      formData.append('term', form.term ?? '')
    }

    this.appendTestimonials(formData, form)

    return apiService.post('/events', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  async update(id: number, form: IEventFields): Promise<EventComplete> {
    const formData = this.buildFormData(form)

    formData.append('use_term', form.use_term ? '1' : '0')
    if (form.use_term) {
      formData.append('term', form.term ?? '')
    }

    this.appendTestimonials(formData, form)

    formData.append('_method', 'PUT')

    return apiService.post(`/events/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  async listForSelect(): Promise<SelectEvent[]> {
    return apiService.get('/events/select').then((response) => response.data)
  }

  async getBySlug(slug: string): Promise<EventDetail> {
    return apiService.get(`/events/slug/${slug}`).then((response) => response.data)
  }

  /** Returns revenue breakdown for an event (by event_slug) or contest (by contest_id). */
  async getRevenueSummary(params: { event_slug?: string; contest_id?: number }): Promise<RevenueSummary> {
    return apiService.get('/events/revenue-summary', { params }).then((response) => response.data)
  }

  /**
   * Tickets do ABRA Park aos quais um evento pode ser associado (ABRA-252).
   * Só os atípicos — os criados para uma ocasião só, que é como o
   * estacionamento de um evento é vendido — mas todos eles, inativos ou fora
   * da vigência inclusive. `selectedId` é o ticket já ligado ao evento: se ele
   * saiu da lista (voltou a ser padrão lá), a API o anexa para o vínculo não
   * perder o nome.
   *
   * As duas defesas cobrem a API anterior ao envelope, que não mandava nenhuma
   * das duas chaves: sem o array vazio, quem consome a lista quebra com
   * TypeError no `.find`/`.map`; e `available` só é lido quando vem de verdade,
   * porque uma API sem a chave não sabe distinguir "o ABRA Park não respondeu"
   * de "não há tickets". Assumir que respondeu é o lado do erro que diz ao
   * operador que o ticket foi excluído e o convida a desfazer um vínculo bom —
   * assumir que não respondeu só mantém o vínculo travado.
   */
  async listParkingTicketTypes(selectedId?: number | null): Promise<ParkingTicketTypesResponse> {
    return apiService
      .get('/events/parking-ticket-types', { params: selectedId ? { selected_id: selectedId } : undefined })
      .then((response) => ({
        data: response.data?.data ?? [],
        available: typeof response.data?.available === 'boolean' ? response.data.available : false
      }))
  }

  /** Updates the manually-entered parking revenue for an event. */
  async updateParkingRevenue(eventId: number, amount: number): Promise<{ parking_revenue: number }> {
    return apiService.patch(`/events/${eventId}/parking-revenue`, { amount }).then((response) => response.data)
  }

  async getParticipants(
    eventId: number,
    params: {
      search?: string
      ticket_id?: number
      page?: number
      page_size?: number
      origin?: string
      paid_value?: number
    }
  ): Promise<ParticipantsResponse> {
    return apiService.get(`/events/${eventId}/participants`, { params }).then((response) => response.data)
  }

  async getParticipantPaidValues(eventId: number): Promise<{ paid_values: number[] }> {
    return apiService.get(`/events/${eventId}/participants-paid-values`).then((response) => response.data)
  }

  async getParticipantTicketQrCode(
    eventId: number,
    participantId: number
  ): Promise<{ token: string; participant_name: string; event_name: string; ticket_name: string }> {
    return apiService.get(`/events/${eventId}/participants/${participantId}/ticket-qrcode`).then((res) => res.data)
  }

  /** Downloads a XLSX file containing the list of paid participants for a specific event. */
  async downloadParticipantsXlsx(id: number): Promise<void> {
    return downloadBlobFromApi(`/events/${id}/participants-xlsx`, `evento_${id}_participantes.xlsx`)
  }

  async getOrders(
    eventId: number,
    params: {
      search?: string
      payment_status?: string
      origin?: string
      contest_id?: number
      page?: number
      page_size?: number
    }
  ): Promise<OrdersResponse> {
    return apiService.get(`/events/${eventId}/orders`, { params }).then((res) => res.data)
  }

  async getOrderDetail(eventId: number, orderUuid: string): Promise<EventOrderDetail> {
    return apiService.get(`/events/${eventId}/orders/${orderUuid}`).then((res) => res.data)
  }

  async cancelOrder(eventId: number, orderUuid: string): Promise<CancelOrderResponse> {
    return apiService.post(`/events/${eventId}/orders/${orderUuid}/cancel`).then((res) => res.data)
  }

  /** Contests linked to the event, used by the origin filter on the orders screen (ABRA-233). */
  async getOrderSources(eventId: number): Promise<EventOrderSourceOption[]> {
    return apiService.get(`/events/${eventId}/order-sources`).then((res) => res.data)
  }

  async getContestOrderDetail(eventId: number, applicationUuid: string): Promise<EventContestOrderDetail> {
    return apiService.get(`/events/${eventId}/contest-orders/${applicationUuid}`).then((res) => res.data)
  }

  async cancelContestOrder(eventId: number, applicationUuid: string): Promise<CancelOrderResponse> {
    return apiService.post(`/events/${eventId}/contest-orders/${applicationUuid}/cancel`).then((res) => res.data)
  }

  async getCaucaoParticipants(eventId: number): Promise<CaucaoParticipantsResponse> {
    return apiService.get(`/events/${eventId}/caucao-participants`).then((res) => res.data)
  }

  async refundCheckedInFreeTickets(
    eventId: number
  ): Promise<{ message: string; refunded_count: number; errors: Array<{ order_uuid: string; error: string }> }> {
    return apiService.post(`/events/${eventId}/refund-free-checked-in`).then((res) => res.data)
  }

  async reconcileCaucaoRefunds(eventId: number): Promise<{
    message: string
    reconciled_count: number
    orders_checked: number
    errors: Array<{ order_uuid: string; error: string }>
  }> {
    return apiService.post(`/events/${eventId}/reconcile-caucao-refunds`).then((res) => res.data)
  }

  async sendTicketEmails(
    eventId: number,
    options: { participantIds?: number[]; sendAll?: boolean }
  ): Promise<{ dispatched: number }> {
    const payload = options.sendAll ? { send_all: true } : { participant_ids: options.participantIds }
    return apiService.post(`/events/${eventId}/participants/send-ticket-emails`, payload).then((res) => res.data)
  }

  /* ─── Certificados de participação (ABRA-343) ─────────── */

  async getCertificate(eventId: number): Promise<EventCertificate> {
    return apiService.get(`/events/${eventId}/certificate`).then((res) => res.data)
  }

  /** O PDF do modelo salvo, para o editor desenhar a 1ª página. */
  async getCertificateTemplate(eventId: number): Promise<ArrayBuffer> {
    return apiService
      .get(`/events/${eventId}/certificate/template`, { responseType: 'arraybuffer' })
      .then((res) => res.data)
  }

  /** Salva o layout e, quando vier, o novo modelo. Sem `template`, o modelo salvo continua. */
  async saveCertificate(
    eventId: number,
    data: { template?: File | null; layout: CertificateLayout }
  ): Promise<EventCertificate> {
    return apiService.post(`/events/${eventId}/certificate`, this.certificateFormData(data)).then((res) => res.data)
  }

  /** Abre numa aba o certificado de exemplo, com o modelo e o layout ainda não salvos, se houver. */
  async openCertificatePreviewPdf(
    eventId: number,
    data: { template?: File | null; layout: CertificateLayout }
  ): Promise<void> {
    return openPostedBlobFromApi(`/events/${eventId}/certificates/preview-pdf`, this.certificateFormData(data))
  }

  async getCertificatesPreview(eventId: number, force = false): Promise<EventCertificatesStats> {
    return apiService.get(`/events/${eventId}/certificates/preview`, { params: { force } }).then((res) => res.data)
  }

  async sendCertificates(eventId: number, payload: EventCertificatesPayload): Promise<EventCertificatesStats> {
    return apiService.post(`/events/${eventId}/certificates`, payload).then((res) => res.data)
  }

  /** Cria o envio agendado do evento ou, se já houver um pendente, substitui (ABRA-364). */
  async saveCertificateSchedule(
    eventId: number,
    payload: EventCertificateSchedulePayload
  ): Promise<EventCertificateSchedule> {
    return apiService.post(`/events/${eventId}/certificates/schedule`, payload).then((res) => res.data)
  }

  async cancelCertificateSchedule(eventId: number): Promise<void> {
    return apiService.delete(`/events/${eventId}/certificates/schedule`).then(() => undefined)
  }

  private certificateFormData(data: { template?: File | null; layout: CertificateLayout }): FormData {
    const formData = new FormData()
    if (data.template) formData.append('template', data.template)
    // O layout vai como JSON: um objeto por texto do certificado, e a API decodifica o campo.
    formData.append('layout', JSON.stringify(data.layout))

    return formData
  }

  async getAttendeesCheckin(
    eventId: number,
    params: { search?: string; no_checkin?: boolean; no_checkout?: boolean; page?: number; page_size?: number }
  ): Promise<AttendeesCheckinResponse> {
    return apiService.get(`/events/${eventId}/attendees-checkin`, { params }).then((res) => res.data)
  }

  async manualCheckin(eventId: number, participantId: number) {
    return apiService
      .post(`/events/${eventId}/manual-checkin`, { participant_id: participantId })
      .then((res) => res.data)
  }

  async manualCheckout(eventId: number, participantId: number) {
    return apiService
      .post(`/events/${eventId}/manual-checkout`, { participant_id: participantId })
      .then((res) => res.data)
  }

  /** What the attendee's active tab owes, as the terminal shows it at checkout (ABRA-425). */
  async getCheckoutInfo(eventId: number, participantId: number): Promise<CheckoutInfo> {
    return apiService.get(`/events/${eventId}/participants/${participantId}/checkout-info`).then((res) => res.data)
  }

  /** Establishment products the check-out may add to the tab. */
  async getCheckoutCatalog(eventId: number): Promise<CheckoutCatalogEstablishment[]> {
    return apiService.get(`/events/${eventId}/checkout-catalog`).then((res) => res.data.establishments)
  }

  /** Registers the payment (with its receipt) and closes the tab. */
  async checkoutWithPayment(eventId: number, formData: FormData): Promise<CheckoutPaymentResult> {
    return apiService.post(`/events/${eventId}/checkout-payment`, formData).then((res) => res.data)
  }

  /**
   * Append the testimonial slots to the multipart body. Each slot keeps its index so
   * the API can line up kept paths and newly uploaded MP4s in the intended order:
   *   - testimonial_videos[i] = the uploaded File for a new/replaced slot
   *   - testimonial_existing[i] = the kept stored path/URL for an unchanged slot
   */
  private appendTestimonials(formData: FormData, form: IEventFields): void {
    formData.append('use_testimonial', form.use_testimonial ? '1' : '0')
    if (!form.use_testimonial) return

    formData.append('testimonial_title', form.testimonial_title ?? '')
    form.testimonial_videos.forEach((file, i) => {
      if (file) formData.append(`testimonial_videos[${i}]`, file)
    })
    form.testimonial_existing.forEach((path, i) => {
      // Only send the kept path when this slot has no new file: a replaced slot must
      // carry testimonial_videos[i] alone, never both, so the payload stays unambiguous.
      if (path && !form.testimonial_videos[i]) {
        formData.append(`testimonial_existing[${i}]`, path)
      }
    })
  }

  private buildFormData(form: IEventFields): FormData {
    const formData = new FormData()

    formData.append('name', form.name)
    formData.append('slug', form.slug)
    formData.append('format', form.format)
    formData.append('date_start', form.date_start)
    formData.append('is_multiday', form.is_multiday ? '1' : '0')
    formData.append('time_start', form.time_start)
    formData.append('time_end', form.time_end)
    formData.append('visible', form.visible ? '1' : '0')
    formData.append('use_nfc', form.use_nfc ? '1' : '0')
    formData.append('use_help_form', form.use_help_form ? '1' : '0')

    if (form.is_multiday && form.date_end) {
      formData.append('date_end', form.date_end)
    } else if (!form.is_multiday) {
      formData.append('date_end', '')
    }

    if (form.thumb) {
      formData.append('thumb', form.thumb)
    }

    if (form.thumb_wide) {
      formData.append('thumb_wide', form.thumb_wide)
    }

    // Event video: send the uploaded MP4 when picked; signal removal explicitly.
    if (form.video) {
      formData.append('video', form.video)
    }
    if (form.remove_video) {
      formData.append('remove_video', '1')
    }

    // `types_provided` always sent so emptying every type is rejected by the API
    // (min:1) instead of silently keeping the old ones — multipart omits empty arrays.
    formData.append('types_provided', '1')
    form.types.forEach((typeId) => {
      formData.append('types[]', typeId.toString())
    })

    // Cursos relacionados (opcional). Array vazio não envia nada; a API limpa as
    // associações no update — mesmo contrato das listas do Active Campaign abaixo.
    form.course_ids.forEach((courseId) => {
      formData.append('course_ids[]', courseId.toString())
    })

    formData.append('copy', form.copy)
    formData.append('target_audience', form.target_audience)
    formData.append('topics', form.topics)

    // AC lists (optional). Empty arrays send nothing; the API clears them on update.
    form.active_campaign_lead_lists.forEach((listId) => {
      formData.append('active_campaign_lead_lists[]', listId)
    })
    form.active_campaign_buyer_lists.forEach((listId) => {
      formData.append('active_campaign_buyer_lists[]', listId)
    })

    // `tickets_provided` always sent so an emptied list clears the tickets on the API
    // (multipart omits empty arrays) instead of leaving the old ones untouched.
    formData.append('tickets_provided', '1')
    form.tickets.forEach((ticket, index) => {
      if (ticket.id) {
        formData.append(`tickets[${index}][id]`, ticket.id.toString())
      }
      formData.append(`tickets[${index}][type]`, ticket.type)
      formData.append(`tickets[${index}][title]`, ticket.title)
      formData.append(`tickets[${index}][quantity]`, ticket.quantity.toString())
      formData.append(`tickets[${index}][price]`, ticket.price.toString())
      formData.append(`tickets[${index}][sales_start]`, ticket.sales_start)
      formData.append(`tickets[${index}][sales_end]`, ticket.sales_end)
      formData.append(`tickets[${index}][max_per_purchase]`, ticket.max_per_purchase.toString())
      formData.append(`tickets[${index}][description]`, ticket.description)
      formData.append(`tickets[${index}][visible]`, ticket.visible ? '1' : '0')
      formData.append(`tickets[${index}][fee_exempt]`, ticket.fee_exempt ? '1' : '0')
      formData.append(`tickets[${index}][is_child_ticket]`, ticket.is_child_ticket ? '1' : '0')
      formData.append(`tickets[${index}][sell_at_door]`, ticket.sell_at_door ? '1' : '0')
      // Vai mesmo com a venda na porta desligada: a API guarda o preço para quando ela voltar.
      formData.append(`tickets[${index}][door_price]`, ticket.door_price !== null ? ticket.door_price.toString() : '')
    })

    // `establishments_provided` pelo mesmo motivo de `tickets_provided`: sem ele, esvaziar a
    // lista não chegaria à API e os estabelecimentos antigos ficariam (ABRA-317).
    formData.append('establishments_provided', '1')
    appendEstablishments(formData, 'establishments', form.establishments)

    form.professionals.forEach((professional, index) => {
      if (professional.teacher_id) {
        formData.append(`professionals[${index}][teacher_id]`, professional.teacher_id.toString())
      } else {
        formData.append(`professionals[${index}][title]`, professional.title)
        formData.append(`professionals[${index}][name]`, professional.name)
        formData.append(`professionals[${index}][bio]`, professional.bio)
        if (professional.thumb) {
          formData.append(`professionals[${index}][thumb]`, professional.thumb)
        }
      }
    })

    // `faqs_provided` always sent so an emptied list clears the FAQs on the API
    // (multipart omits empty arrays). `position` follows the submitted order.
    formData.append('faqs_provided', '1')
    form.faqs.forEach((faq, index) => {
      if (faq.id) {
        formData.append(`faqs[${index}][id]`, faq.id.toString())
      }
      formData.append(`faqs[${index}][question]`, faq.question)
      formData.append(`faqs[${index}][answer]`, faq.answer)
      formData.append(`faqs[${index}][position]`, index.toString())
    })

    if (form.format === 'IN_PERSON' || form.format === 'HYBRID') {
      formData.append('zipcode', form.zipcode)
      formData.append('street', form.street)
      formData.append('number', form.number)
      formData.append('complement', form.complement)
      formData.append('neighborhood', form.neighborhood)
      formData.append('city', form.city)
      formData.append('state', form.state)
    }

    if (form.latitude !== null) {
      formData.append('latitude', form.latitude.toString())
    }
    if (form.longitude !== null) {
      formData.append('longitude', form.longitude.toString())
    }

    formData.append('place_id', form.place_id ?? '')
    formData.append('place_name', form.place_name ?? '')

    // A coluna é NOT NULL com default 5 — limpar o campo não pode virar null (o
    // banco rejeitaria), então volta para o próprio default, igual ao que a API já
    // faz na criação (`$validated['min_installment_value'] ?? 5`). Sempre enviado:
    // sem isso, o campo nunca some depois de definido, pois multipart não envia
    // nada para um valor null.
    formData.append(
      'min_installment_value',
      form.min_installment_value !== null ? form.min_installment_value.toString() : '5'
    )

    // String vazia limpa a associação: a API converte para null
    // (ConvertEmptyStringsToNull), mesmo contrato do date_end acima.
    formData.append(
      'parking_ticket_type_id',
      form.parking_ticket_type_id !== null ? form.parking_ticket_type_id.toString() : ''
    )

    return formData
  }
}
